'use client';

/**
 * Guards a lesson URL.
 *
 * Lesson pages are statically generated, so anyone can navigate straight to
 * lesson 40 by typing the URL. That is fine for a crawler but wrong for a
 * learner: the exercise would contain keys they have never been taught. This
 * component checks the unlock rule on the client (where progress lives) and
 * offers the correct next lesson instead of silently failing.
 */
import React from 'react';
import Link from 'next/link';
import { useKids } from '../context/KidsProvider';
import { isLessonUnlocked, nextUpLesson } from '../lib/progress';
import { getLesson, getLevel } from '../data/curriculum';
import type { LessonDef, LevelDef } from '../types';
import { LoadingBlock } from './ui';

export default function LessonGate({
  lesson,
  level,
  children,
}: {
  lesson: LessonDef;
  level: LevelDef;
  children: React.ReactNode;
}) {
  const { hydrated, profile } = useKids();

  if (!hydrated) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <LoadingBlock height={420} label="Getting your lesson ready" />
      </div>
    );
  }

  if (isLessonUnlocked(profile, lesson.id)) return <>{children}</>;

  const suggestedId = nextUpLesson(profile);
  const suggested = getLesson(suggestedId);
  const suggestedLevel = suggested ? getLevel(suggested.levelId) : null;

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
      <div className="k-card-lg p-7 text-center">
        <div style={{ fontSize: 48 }} aria-hidden="true">
          🔒
        </div>
        <h1 className="mt-3 text-xl">This lesson is still locked</h1>
        <p className="text-muted-foreground mt-2">
          “{lesson.title}” is part of Level {level.index}: {level.title}. It uses keys you have not
          met yet — finishing the lessons before it will unlock it automatically.
        </p>

        <div className="flex flex-wrap gap-2 justify-center mt-6">
          {suggested && suggestedLevel && (
            <Link href={`/kids/lesson/${suggested.id}`} className="k-btn k-btn-primary">
              Go to my next lesson: {suggested.title}
            </Link>
          )}
          <Link href="/kids/journey" className="k-btn k-btn-secondary">
            See the whole journey
          </Link>
        </div>
      </div>
    </div>
  );
}
